/**
 * AK-938 — подписи «Науки» на языке агронома (low-fi 21.10, утверждён founder).
 * Чистые функции: без React и сети — юнит-тестируемы.
 */
import type {
  CheckValueKind,
  TrialFactor,
  TrialInput,
  TrialStatus,
  VariantRole,
} from './types';

export const FACTOR_LABELS: Record<TrialFactor, string> = {
  crop_protection: 'СЗР',
  fertilizer: 'Удобрения',
  variety: 'Сортоиспытание',
  tillage: 'Обработка почвы',
  operation_timing: 'Сроки тех.операций',
};

export const FACTOR_ORDER: TrialFactor[] = [
  'crop_protection',
  'fertilizer',
  'variety',
  'tillage',
  'operation_timing',
];

export const ROLE_LABELS: Record<VariantRole, string> = {
  control: 'Контроль',
  reference: 'Эталон',
  test: 'Опытный',
};

export const STATUS_LABELS: Record<TrialStatus, string> = {
  planned: 'Запланирован',
  laid: 'Заложен',
  closed_yield: 'Закрыт с урожайностью',
  closed_incomplete: 'Закрыт как недоделанный',
};

export const CHECK_KIND_LABELS: Record<CheckValueKind, string> = {
  number: 'число',
  choice: 'выбор из списка',
  text: 'текст',
  photo: 'фото',
};

export function isClosed(status: TrialStatus | null | undefined): boolean {
  return status === 'closed_yield' || status === 'closed_incomplete';
}

/**
 * Короткая метка варианта для карты и таблиц: «К» — контроль, «Э» — эталон,
 * опытные — 1, 2, … по порядку среди опытных.
 */
export function variantMarks(roles: Array<VariantRole | null | undefined>): string[] {
  let n = 0;
  return roles.map((r) => {
    if (r === 'control') return 'К';
    if (r === 'reference') return 'Э';
    n += 1;
    return String(n);
  });
}

/** Число без хвоста: 0,8 · 20 · 0,25 (запятая — как пишет агроном). */
export function formatRate(rate: number): string {
  return rate.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
}

/** «Тест-Гран, ВДГ · 20 г/га · кущение». */
export function formatInput(input: TrialInput): string {
  const parts: string[] = [input.product_name || 'препарат'];
  if (input.rate != null) {
    const unit = input.rate_unit_symbol ? ` ${input.rate_unit_symbol}` : '';
    const basis = input.rate_basis === 'per_t_seed' ? ' на т семян' : '';
    parts.push(`${formatRate(input.rate)}${unit}${basis}`);
  }
  if (input.timing_note?.trim()) parts.push(input.timing_note.trim());
  return parts.join(' · ');
}

/** Что применяем в варианте: препараты через «+», иначе пометка, иначе прочерк. */
export function variantTreatment(inputs: TrialInput[], note?: string | null): string {
  if (inputs.length) return inputs.map(formatInput).join(' + ');
  return note?.trim() || '—';
}

/** Гектары: 0,90 га. */
export function formatHa(ha: number | null | undefined): string {
  if (ha == null) return '—';
  return `${ha.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} га`;
}

/** ц/га с одним знаком; прибавка — со знаком. */
export function formatCHa(v: number | null | undefined, signed = false): string {
  if (v == null) return '—';
  const s = v.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return signed && v > 0 ? `+${s}` : s;
}
